/**
 * The bundle-generic half of the registry shape — docs/infra/bundle-contract.md.
 *
 * A "bundle" is one OpenTofu root under `infra/bundles/<type>/` that a colleague gets their own copy
 * of (AVD and SWA). Every bundle's registry row carries the same five version/drift facts, with the
 * same column names, so desired-vs-actual (#1060), the drift check (#1266) and roll-forward (#1062) are
 * written ONCE and work for every bundle. Each registry imports these rather than redefining them.
 *
 * It lives in avd-common only because that is the one isomorphic package the registry, the
 * scaffolder and the frontend already share; nothing in this file is AVD-specific. Move it to its own
 * package when a second consumer makes that worth the scaffolding.
 *
 * The columns, snake_case, on every bundle registry table:
 *
 * | column             | field            | written by                                      |
 * | ------------------ | ---------------- | ----------------------------------------------- |
 * | `bundle_type`      | `bundleType`     | the table default; = the bundle's key in `infra/bundles.json` |
 * | `bundle_id`        | `bundleId`       | the table default `''`; a type with several, the provision workflow |
 * | `iac_version`      | `iacVersion`     | the provision workflow, from the root's `iac_version` output |
 * | `drift_status`     | `driftStatus`    | `bundle-drift.yaml`, via `PUT /registry/:bundleType/drift/:slug` |
 * | `drift_checked_at` | `driftCheckedAt` | `bundle-drift.yaml` (the server's time of the write) |
 */

/** A bundle type — the root's key in `infra/bundles.json` and its `bundle_type` output. */
export type BundleType = "avd";

/**
 * Every member of {@link BundleType}: the types with a lifecycle. A new bundle adds its key here, to
 * `infra/bundles.json` and to {@link BUNDLE_REGISTRY_TABLES}. The colleague base is in the last two only.
 */
export const BUNDLE_TYPES: readonly BundleType[] = ["avd"];

/** Narrows an arbitrary string to {@link BundleType}. */
export function isBundleType(value: unknown): value is BundleType {
	return typeof value === "string" && (BUNDLE_TYPES as readonly string[]).includes(value);
}

/**
 * The registry table of every type in `infra/bundles.json`, the colleague base included. Held to the
 * manifest by `workflowContract.test.ts` — docs/infra/bundle-contract.md § Registry columns.
 */
export const BUNDLE_REGISTRY_TABLES = {
	avd: "avd_desktops",
	base: "colleague_bases",
	swa: "swa_bundles",
} as const;

/** A type with a registry table: every key of `infra/bundles.json`. */
export type RegisteredBundleType = keyof typeof BUNDLE_REGISTRY_TABLES;

/** Narrows an arbitrary string to {@link RegisteredBundleType}. */
export function isRegisteredBundleType(value: unknown): value is RegisteredBundleType {
	return typeof value === "string" && Object.hasOwn(BUNDLE_REGISTRY_TABLES, value);
}

/**
 * Who reports a base: its own `apply`, or the registry sync's `discovery` from subscription tags, which only
 * creates a missing row — docs/app/avd-registry.md § The colleague base.
 */
export type ColleagueBaseSource = "apply" | "discovery";

/** Every member of {@link ColleagueBaseSource}. */
export const COLLEAGUE_BASE_SOURCES: readonly ColleagueBaseSource[] = ["apply", "discovery"];

/** Body of `PUT /registry/base/:slug`: what `colleague-base.yaml` and the registry sync know. */
export type ColleagueBaseUpdate = {
	subscriptionId: string;
	/** The base's `iac_version` output. Absent leaves the recorded one alone. */
	iacVersion?: string;
	source: ColleagueBaseSource;
};

/** Response of `PUT /registry/base/:slug`. `false`: a discovery of a base already recorded or destroyed. */
export type ColleagueBaseUpdateResponse = { recorded: boolean };

const SUBSCRIPTION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Why a {@link ColleagueBaseUpdate} body is refused; empty when it is valid. */
export function colleagueBaseUpdateErrors(body: unknown): string[] {
	const b = (typeof body === "object" && body !== null ? body : {}) as Record<string, unknown>;
	const errors: string[] = [];
	if (typeof b.subscriptionId !== "string" || !SUBSCRIPTION_ID.test(b.subscriptionId)) {
		errors.push("subscriptionId must be a subscription GUID");
	}
	if (b.iacVersion !== undefined && (typeof b.iacVersion !== "string" || b.iacVersion === "")) {
		errors.push("iacVersion, when given, must be a non-empty string");
	}
	if (!(COLLEAGUE_BASE_SOURCES as readonly unknown[]).includes(b.source)) {
		errors.push(`source must be one of ${COLLEAGUE_BASE_SOURCES.join(", ")}`);
	}
	return errors;
}

/**
 * A bundle id as a source value may spell it: 1-12 ASCII letters, digits or hyphens, starting with a
 * letter or digit. Its canonical form is the same, lowercased — docs/infra/bundle-contract.md § Outputs.
 */
export const BUNDLE_ID_SOURCE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9-]{0,11}$/;

/**
 * The canonical bundle id; throws on an invalid one. Shell twin: `.github/scripts/lib/bundle-id.sh`,
 * pinned to this by `bundleIdCases.json`.
 */
export function canonicalBundleId(raw: string): string {
	// Checked before lowercasing: "K" (Kelvin) lowercases to an ASCII "k" the shell would refuse.
	if (!BUNDLE_ID_SOURCE_PATTERN.test(raw)) {
		throw new Error(
			`invalid bundle id "${raw}": 1-12 letters, digits or hyphens, starting with a letter or digit`,
		);
	}
	return raw.toLowerCase();
}

/**
 * Why a bundle id cannot join a colleague's existing ones of its type, or `undefined` when it can.
 * Ids differing only by case would share a state key — docs/infra/bundle-contract.md § Outputs.
 */
export function bundleIdConflict(
	existingSourceIds: Iterable<string>,
	candidateSourceId: string,
): string | undefined {
	const canonical = canonicalBundleId(candidateSourceId);
	for (const existing of existingSourceIds) {
		if (existing !== candidateSourceId && existing.toLowerCase() === canonical) {
			return `bundle id "${candidateSourceId}" differs only by case from the existing "${existing}"; use "${existing}"`;
		}
	}
	return undefined;
}

/**
 * What the last drift check said about a bundle — docs/infra/bundle-contract.md § Drift check.
 *
 * - `in-sync` — a refresh-only plan found nothing changed in Azure since the last apply.
 * - `drifted` — it found an out-of-band change. Code main has not applied yet never counts.
 * - `error` — the plan itself failed. Says nothing about the bundle; the run is the truth.
 *
 * No value at all means "never checked", which is different from all three.
 */
export type BundleDriftStatus = "in-sync" | "drifted" | "error";

/** Every member of {@link BundleDriftStatus}, for runtime validation and workflow contract tests. */
export const BUNDLE_DRIFT_STATUSES: readonly BundleDriftStatus[] = ["in-sync", "drifted", "error"];

/** Narrows an arbitrary string to {@link BundleDriftStatus}. */
export function isBundleDriftStatus(value: unknown): value is BundleDriftStatus {
	return (
		typeof value === "string" && (BUNDLE_DRIFT_STATUSES as readonly string[]).includes(value)
	);
}

/** Body of `PUT /registry/:bundleType/drift/:slug`: one drift check's verdict on one bundle. */
export type BundleDriftUpdate = {
	/** As the state key spells it; absent for the only bundle of its type. */
	bundleId?: string;
	status: BundleDriftStatus;
};

/** Service-only: where the drift check writes one bundle's verdict (its id goes in the body). */
export function bundleDriftPath(bundleType: string, slug: string): string {
	return `/registry/${encodeURIComponent(bundleType)}/drift/${encodeURIComponent(slug)}`;
}

/** Response of `PUT /registry/:bundleType/drift/:slug`. */
export type BundleDriftUpdateResponse = { recorded: true };

/** Why a {@link BundleDriftUpdate} body is refused; empty when it is valid. */
export function bundleDriftUpdateErrors(body: unknown): string[] {
	const b = (typeof body === "object" && body !== null ? body : {}) as Record<string, unknown>;
	const errors: string[] = [];
	// The canonical form only: the state key, and so the row, never holds another spelling.
	if (
		b.bundleId !== undefined &&
		(typeof b.bundleId !== "string" ||
			!BUNDLE_ID_SOURCE_PATTERN.test(b.bundleId) ||
			b.bundleId !== b.bundleId.toLowerCase())
	) {
		errors.push("bundleId, when given, must be a canonical bundle id");
	}
	if (!isBundleDriftStatus(b.status)) {
		errors.push(`status must be one of ${BUNDLE_DRIFT_STATUSES.join(", ")}`);
	}
	return errors;
}

/** The version and drift facts every bundle registry row carries, as the API returns them. */
export type BundleVersionFacts = {
	bundleType: BundleType;
	/**
	 * Which of the colleague's bundles of this type — e.g. the project number for SWA. Absent for a
	 * bundle there is only ever one of per colleague (AVD), matching its empty `bundle_id` output.
	 */
	bundleId?: string;
	/**
	 * The bundle IaC version (ADR-0020) it was last applied from — the same value as its RG's
	 * `iac-version` tag and the root's `iac_version` output. Absent on rows that never recorded one.
	 */
	iacVersion?: string;
	/** The last drift check's verdict. Absent = never checked. */
	driftStatus?: BundleDriftStatus;
	/** ISO-8601. When {@link driftStatus} was last written. */
	driftCheckedAt?: string;
};

/**
 * Desired vs actual IaC version, the answer #1060 shows and #1062 rolls forward on.
 *
 * - `current` — applied from the version `main` would apply now.
 * - `behind` — applied from an older (or just different) version. Versions are content hashes, so
 *   there is no "ahead": any difference means the bundle's IaC changed since.
 * - `unknown` — either side is missing, so we cannot say. Never reported as `behind`: a row from
 *   before versions were recorded is not evidence of anything.
 */
export type BundleVersionState = "current" | "behind" | "unknown";

/** Compares a bundle's recorded version with the desired one. Exact equality (ADR-0020). */
export function bundleVersionState(desired?: string, actual?: string): BundleVersionState {
	if (!desired || !actual) return "unknown";
	return desired === actual ? "current" : "behind";
}

/** The registry columns every bundle table carries for version and drift (above), snake_case. */
export const BUNDLE_VERSION_COLUMNS = [
	"bundle_type",
	"bundle_id",
	"iac_version",
	"drift_status",
	"drift_checked_at",
] as const;

/** The lifecycle columns every bundle registry table carries — docs/infra/bundle-contract.md § Lifecycle. */
export const BUNDLE_LIFECYCLE_COLUMNS = [
	"last_seen_at",
	"last_seen_source",
	"lifecycle",
	"notice_stage",
	"notice_stage_at",
	"notice_deadline",
	"hold_until",
	"hold_reason",
	"hold_set_by",
	"hold_notice_for",
] as const;

/**
 * The owner columns every lifecycle bundle registry table carries: the self-service routes match them to the
 * caller, because distinct emails can share a slug — docs/infra/bundle-contract.md § Owner columns.
 */
export const BUNDLE_OWNER_COLUMNS = ["upn", "entity_ref"] as const;

/** The lifecycle fields the nightly registry sync must never write: only the warning and holds do. */
export const BUNDLE_SYNC_FORBIDDEN_FIELDS = [
	"lifecycle",
	"noticeStage",
	"noticeStageAt",
	"noticeDeadline",
	"holdUntil",
	"holdReason",
	"holdSetBy",
	"holdNoticeFor",
] as const;

/** The bundle-contract columns a registry table lacks, given its column names. Empty = it conforms. */
export function missingBundleColumns(columns: Iterable<string>): string[] {
	const present = new Set(columns);
	return [...BUNDLE_VERSION_COLUMNS, ...BUNDLE_LIFECYCLE_COLUMNS, ...BUNDLE_OWNER_COLUMNS].filter(
		(c) => !present.has(c),
	);
}

/** {@link missingBundleColumns} for a type with no lifecycle (the colleague base): version and drift only. */
export function missingBundleVersionColumns(columns: Iterable<string>): string[] {
	const present = new Set(columns);
	return BUNDLE_VERSION_COLUMNS.filter((c) => !present.has(c));
}

/** Where `last_seen_at` came from — docs/infra/bundle-contract.md § Lifecycle columns. */
export type BundleLastSeenSource = "signal" | "created_at" | "unknown";

/** Every member of {@link BundleLastSeenSource}. */
export const BUNDLE_LAST_SEEN_SOURCES: readonly BundleLastSeenSource[] = [
	"signal",
	"created_at",
	"unknown",
];

/** Narrows an arbitrary string to {@link BundleLastSeenSource}. */
export function isBundleLastSeenSource(value: unknown): value is BundleLastSeenSource {
	return (
		typeof value === "string" && (BUNDLE_LAST_SEEN_SOURCES as readonly string[]).includes(value)
	);
}

/**
 * Whether a bundle is on the inactivity clock. Separate from `status`, which is readiness: an
 * `expiring` bundle is still `ready`.
 */
export type BundleLifecycle = "active" | "expiring";

/** Every member of {@link BundleLifecycle}. */
export const BUNDLE_LIFECYCLES: readonly BundleLifecycle[] = ["active", "expiring"];

/** Narrows an arbitrary string to {@link BundleLifecycle}. */
export function isBundleLifecycle(value: unknown): value is BundleLifecycle {
	return typeof value === "string" && (BUNDLE_LIFECYCLES as readonly string[]).includes(value);
}

/** The inactivity notice sent for a bundle (#969): there is one, the warning. Absent = none this cycle. */
export type BundleNoticeStage = "warn";

/** Every member of {@link BundleNoticeStage}. */
export const BUNDLE_NOTICE_STAGES: readonly BundleNoticeStage[] = ["warn"];

/** Narrows an arbitrary string to {@link BundleNoticeStage}. */
export function isBundleNoticeStage(value: unknown): value is BundleNoticeStage {
	return typeof value === "string" && (BUNDLE_NOTICE_STAGES as readonly string[]).includes(value);
}

/** The lifecycle facts every bundle registry row carries, as the API returns them. */
export type BundleLifecycleFacts = {
	/** ISO-8601. The latest use the type's signal saw, floored at creation. Absent = never read. */
	lastSeenAt?: string;
	lastSeenSource?: BundleLastSeenSource;
	lifecycle: BundleLifecycle;
	noticeStage?: BundleNoticeStage;
	/** ISO-8601. When {@link noticeStage} was sent. */
	noticeStageAt?: string;
	/** ISO-8601. The reclaim date the warning named; stored once and never advanced (ADR-0024). */
	noticeDeadline?: string;
	/** ISO-8601. The bundle is exempt until then (#970). Holds are always dated. */
	holdUntil?: string;
	holdReason?: string;
	/** Catalog ref of whoever placed the hold. */
	holdSetBy?: string;
	/** ISO-8601. The hold end the expiry notice was sent for; a changed hold makes it stale. */
	holdNoticeFor?: string;
};

/** One bundle as the lifecycle read route returns it: identity, owner, status and lifecycle facts. */
export type BundleLifecycleItem = BundleLifecycleFacts & {
	bundleType: string;
	bundleId?: string;
	slug: string;
	/** `resolved`: the lifecycle read matched the UPN to a catalog user. Anything else is unresolved. */
	owner: { upn?: string; entityRef?: string; resolved?: boolean; displayName?: string };
	/** The registry's own status vocabulary for this type. */
	status: string;
	/** ISO-8601. When the current bundle was built: the idle floor. Absent = unknown, so never a verdict. */
	createdAt?: string;
};

/** The bundle field a usage-signal result row is matched on. */
export type BundleSignalKeyField = "slug" | "bundleId" | "owner.upn";

/** How a bundle type's use is read: one KQL query, and how its rows map onto bundles. */
export type BundleUsageSignal = {
	/** What counts as use, in plain language. */
	description: string;
	/** KQL against the central Log Analytics workspace; the caller sets the time range. */
	query: string;
	/** The result column that identifies the bundle, and the bundle field it equals (case-insensitive). */
	bundleKey: { column: string; field: BundleSignalKeyField };
	/** The result column holding the latest use. */
	lastSeenColumn: string;
};

/** One way a skip condition matches: a bundle field equal to a repo variable's value, or starting with a prefix. */
export type BundleSkipMatcher =
	| { field: BundleSignalKeyField; equalsRepoVariable: string }
	| { field: BundleSignalKeyField; prefix: string };

/** A reason to skip a bundle beyond its status. It holds when any of its matchers matches. */
export type BundleSkipCondition = {
	id: string;
	description: string;
	matchAny: readonly BundleSkipMatcher[];
};

/** A bundle's value for a signal or skip field, lower-cased; absent when the bundle has none. */
export function bundleFieldValue(
	item: Pick<BundleLifecycleItem, "slug" | "bundleId" | "owner">,
	field: BundleSignalKeyField,
): string | undefined {
	let value: string | undefined;
	if (field === "slug") value = item.slug;
	else if (field === "bundleId") value = item.bundleId;
	else value = item.owner.upn;
	return value ? value.toLowerCase() : undefined;
}

/** The ids of the skip conditions that hold for a bundle. An unset repo variable matches nothing. */
export function activeSkipConditions(
	descriptor: Pick<BundleLifecycleDescriptor, "skipConditions">,
	item: Pick<BundleLifecycleItem, "slug" | "bundleId" | "owner">,
	repoVariables: Readonly<Record<string, string | undefined>>,
): string[] {
	const matches = (m: BundleSkipMatcher) => {
		const value = bundleFieldValue(item, m.field);
		if (!value) return false;
		if ("prefix" in m) return value.startsWith(m.prefix.toLowerCase());
		const wanted = repoVariables[m.equalsRepoVariable]?.trim().toLowerCase();
		return Boolean(wanted) && value === wanted;
	};
	return descriptor.skipConditions.filter((c) => c.matchAny.some(matches)).map((c) => c.id);
}

/**
 * What a bundle type declares to be evaluated for inactivity — docs/infra/bundle-contract.md §
 * Lifecycle descriptor. No backup field: no bundle type has one.
 */
export type BundleLifecycleDescriptor = {
	bundleType: string;
	/** What a colleague calls one bundle of this type, in a notice: "desktop", "web app". */
	displayName: string;
	/** The owner's page for the type, a Backstage path: notices link there. */
	ownerPagePath: string;
	usageSignal: BundleUsageSignal;
	/** Days idle at which a bundle is due for its terminal action. */
	inactivityWindowDays: number;
	/** Working days between the one warning and the reclaim (ADR-0024 § reclaim calendar). */
	warningLeadWorkingDays: number;
	/** Statuses that mean a bundle exists and is idle-measurable. Every other status is skipped. */
	evaluatedStatuses: readonly string[];
	/** Further reasons to skip a bundle, beyond its status (for AVD, the canary's desktop). */
	skipConditions: readonly BundleSkipCondition[];
	/**
	 * The one workflow that reclaims a single bundle. Input values may use `{slug}`, `{bundleId}`,
	 * `{entityRef}` and `{reclaimContext}` (bundle-contract.md § Reclaim), filled from the bundle's verdict.
	 */
	terminalAction: { workflow: string; inputs: Readonly<Record<string, string>> };
	/** Repo variable that turns enforcement on: `INACTIVITY_ENFORCE_<TYPE>`, off unless `true`. */
	enforceSwitch: string;
};

/** Response body of the service-only lifecycle read, one bundle type per call. */
export type BundleLifecycleResponse = {
	bundleType: string;
	descriptor: BundleLifecycleDescriptor;
	/** ISO-8601. When the type's nightly registry sync last completed. Absent = never recorded. */
	syncCompletedAt?: string;
	/** The bundle keys the last delivered platform-ops digest listed. Absent = none delivered yet. */
	digestDelivered?: string[];
	items: BundleLifecycleItem[];
};

const DESCRIPTOR_KEYS: Record<keyof BundleLifecycleDescriptor, true> = {
	bundleType: true,
	displayName: true,
	ownerPagePath: true,
	usageSignal: true,
	inactivityWindowDays: true,
	warningLeadWorkingDays: true,
	evaluatedStatuses: true,
	skipConditions: true,
	terminalAction: true,
	enforceSwitch: true,
};

const SIGNAL_KEY_FIELDS = new Set<string>(["slug", "bundleId", "owner.upn"]);
const TERMINAL_INPUT_PLACEHOLDER = /\{([^}]*)\}/g;
const TERMINAL_INPUT_FIELDS = new Set(["slug", "bundleId", "entityRef", "reclaimContext"]);

/** The switch name a bundle type must use: `INACTIVITY_ENFORCE_` plus the type, upper-cased. */
export function inactivityEnforceSwitch(bundleType: string): string {
	return `INACTIVITY_ENFORCE_${bundleType.toUpperCase().replace(/[^A-Z0-9]/g, "_")}`;
}

/** Every way one skip condition breaks the contract. */
function skipConditionErrors(c: BundleSkipCondition): string[] {
	const errors: string[] = [];
	if (!c.id.trim() || c.matchAny.length === 0) {
		errors.push(`skip condition ${c.id || "(no id)"} needs an id and at least one matcher`);
	}
	for (const m of c.matchAny) {
		const ok =
			SIGNAL_KEY_FIELDS.has(m.field) &&
			("prefix" in m
				? m.prefix.trim() !== ""
				: /^[A-Z][A-Z0-9_]*$/.test(m.equalsRepoVariable));
		if (!ok) errors.push(`skip condition ${c.id} has an invalid matcher`);
	}
	return errors;
}

/** Every way a descriptor breaks the contract. Empty = valid. */
export function lifecycleDescriptorErrors(descriptor: BundleLifecycleDescriptor): string[] {
	const errors: string[] = [];
	const d = descriptor;
	for (const key of Object.keys(d)) {
		if (!(key in DESCRIPTOR_KEYS)) errors.push(`unknown field ${key}`);
	}
	if (!d.displayName?.trim()) errors.push("displayName is required");
	if (!/^\/[a-z0-9/-]*$/.test(d.ownerPagePath ?? "")) {
		errors.push("ownerPagePath must be a Backstage path starting with /");
	}
	const signal = d.usageSignal;
	if (!signal.description.trim() || !signal.query.trim()) {
		errors.push("usageSignal needs a description and a query");
	}
	if (!signal.bundleKey.column.trim() || !SIGNAL_KEY_FIELDS.has(signal.bundleKey.field)) {
		errors.push(
			"usageSignal.bundleKey needs a column and a field of slug, bundleId or owner.upn",
		);
	}
	if (!signal.lastSeenColumn.trim()) errors.push("usageSignal.lastSeenColumn is required");
	const positive = (n: number) => Number.isInteger(n) && n > 0;
	if (!positive(d.inactivityWindowDays))
		errors.push("inactivityWindowDays must be a positive integer");
	if (!positive(d.warningLeadWorkingDays) || d.warningLeadWorkingDays >= d.inactivityWindowDays) {
		errors.push("warningLeadWorkingDays must be a positive integer below inactivityWindowDays");
	}
	if (d.evaluatedStatuses.length === 0) errors.push("evaluatedStatuses must not be empty");
	for (const c of d.skipConditions) errors.push(...skipConditionErrors(c));
	if (!d.terminalAction.workflow.endsWith(".yaml")) {
		errors.push("terminalAction.workflow must be a workflow file name");
	}
	for (const [input, value] of Object.entries(d.terminalAction.inputs)) {
		for (const [, field] of value.matchAll(TERMINAL_INPUT_PLACEHOLDER)) {
			if (!TERMINAL_INPUT_FIELDS.has(field!)) {
				errors.push(`terminalAction.inputs.${input} uses unknown placeholder {${field}}`);
			}
		}
	}
	if (d.enforceSwitch !== inactivityEnforceSwitch(d.bundleType)) {
		errors.push(`enforceSwitch must be ${inactivityEnforceSwitch(d.bundleType)}`);
	}
	return errors;
}

/**
 * One reclaim, appended to `bundle_reclamations` when a terminal action runs (#972 for AVD). Rows are
 * never updated or deleted: the log is how a reclaim is reconstructed afterwards.
 */
export type BundleReclamation = {
	bundleType: string;
	bundleId?: string;
	slug: string;
	owner: { upn?: string; entityRef?: string };
	daysIdle: number;
	lastSeenAt?: string;
	lastSeenSource: BundleLastSeenSource;
	notices: { stage: BundleNoticeStage; sentAt: string }[];
	/** The terminal-action run. */
	runUrl?: string;
	/** Whether the enforce switch was on. A report-only record says `false`. */
	enforced: boolean;
	/** The per-run action cap in force. */
	actionCap: number;
};

/** A {@link BundleReclamation} as stored, with its id and when the server recorded it. */
export type BundleReclamationRecord = BundleReclamation & { id: number; recordedAt: string };

/** Response body of `GET /reclamations/mine`: the caller's own rows, newest first. */
export type MyBundleReclamationsResponse = { items: BundleReclamationRecord[] };

const ISO_TIMESTAMP =
	/^(\d{4})-(\d{2})-(\d{2})T([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d)(?:\.\d+)?)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/;

/** A timezone-qualified ISO-8601 timestamp on a real calendar date. `Date.parse` alone rolls 30 Feb over. */
export function isIsoTimestamp(value: unknown): value is string {
	const m = typeof value === "string" ? ISO_TIMESTAMP.exec(value) : null;
	if (!m) return false;
	const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
	const date = new Date(Date.UTC(year, month - 1, day));
	return (
		date.getUTCFullYear() === year &&
		date.getUTCMonth() === month - 1 &&
		date.getUTCDate() === day
	);
}

const isIso = isIsoTimestamp;
const isOptionalString = (value: unknown) => value === undefined || typeof value === "string";
const isCount = (value: unknown) => Number.isInteger(value) && (value as number) >= 0;

/** Every way a POSTed body fails to be a {@link BundleReclamation}. Empty = valid. */
export function bundleReclamationErrors(value: unknown): string[] {
	if (typeof value !== "object" || value === null) return ["body must be an object"];
	const r = value as Record<string, unknown>;
	const errors: string[] = [];
	if (typeof r.bundleType !== "string" || r.bundleType === "")
		errors.push("bundleType is required");
	if (typeof r.slug !== "string" || r.slug === "") errors.push("slug is required");
	if (!isOptionalString(r.bundleId)) errors.push("bundleId must be a string");
	else if (r.bundleId && !BUNDLE_ID_SOURCE_PATTERN.test(r.bundleId as string)) {
		errors.push("bundleId is not a valid bundle id");
	}
	const owner = r.owner as Record<string, unknown> | undefined;
	if (typeof owner !== "object" || owner === null) errors.push("owner is required");
	else if (!isOptionalString(owner.upn) || !isOptionalString(owner.entityRef)) {
		errors.push("owner.upn and owner.entityRef must be strings");
	}
	if (!isCount(r.daysIdle)) errors.push("daysIdle must be a non-negative integer");
	if (r.lastSeenAt !== undefined && !isIso(r.lastSeenAt))
		errors.push("lastSeenAt must be ISO-8601");
	if (!isBundleLastSeenSource(r.lastSeenSource))
		errors.push("lastSeenSource is not a known source");
	if (
		!Array.isArray(r.notices) ||
		!r.notices.every(
			(n: { stage?: unknown; sentAt?: unknown }) =>
				isBundleNoticeStage(n?.stage) && isIso(n?.sentAt),
		)
	) {
		errors.push("notices must be a list of { stage, sentAt }");
	}
	if (!isOptionalString(r.runUrl)) errors.push("runUrl must be a string");
	if (typeof r.enforced !== "boolean") errors.push("enforced must be a boolean");
	if (!isCount(r.actionCap)) errors.push("actionCap must be a non-negative integer");
	return errors;
}

/** Service-only lifecycle read for one bundle type, relative to the registry plugin's base URL. */
export function bundleLifecyclePath(bundleType: string): string {
	return `/lifecycle/${encodeURIComponent(bundleType)}`;
}

/** Service-only: the type's nightly registry sync reports a successful completion here. */
export function bundleSyncCompletedPath(bundleType: string): string {
	return `${bundleLifecyclePath(bundleType)}/sync-completed`;
}

/** The archive facts of a row, as the API returns them. Absent `archivedAt` = not archived. */
export type BundleArchiveFacts = {
	/** ISO-8601. */
	archivedAt?: string;
	/** Catalog ref of the admin who archived the row. */
	archivedBy?: string;
	archiveReason?: string;
};

/** One bundle as the admin inventory returns it: the lifecycle row plus the registry's version facts. */
export type BundleInventoryItem = BundleLifecycleItem &
	BundleArchiveFacts & {
		iacVersion?: string;
		driftStatus?: BundleDriftStatus;
		/** ISO-8601. */
		driftCheckedAt?: string;
		/** ISO-8601. Last registry write for the row. */
		updatedAt?: string;
		/** A type's own facts, read only by that type's frontend descriptor; the generic table ignores it. */
		details?: unknown;
	};

/** One registered type in the inventory. `available: false`: its live read failed, so its rows may be stale. */
export type BundleInventoryType = {
	bundleType: string;
	displayName: string;
	/** ISO-8601. When a live read of the type happened; absent when none did. */
	readAt?: string;
	available: boolean;
	/** The type's source threw: its rows are missing from this response, which says nothing about whether it has any. */
	failed?: boolean;
};

/** A colleague's base, as the group-by-colleague view shows it. */
export type ColleagueBaseSummary = {
	slug: string;
	subscriptionId?: string;
	iacVersion?: string;
	driftStatus?: BundleDriftStatus;
	driftCheckedAt?: string;
	/** ISO-8601. Set once the base is destroyed; the row stays as the tombstone. */
	destroyedAt?: string;
	updatedAt: string;
};

/** Response of the admin inventory read: every registered type's rows and every colleague base. */
export type BundleInventoryResponse = {
	types: BundleInventoryType[];
	items: BundleInventoryItem[];
	bases: ColleagueBaseSummary[];
};

/** Admin-only inventory read across every registered bundle type, relative to the registry plugin's base URL. */
export const BUNDLE_INVENTORY_PATH = "/bundles";

/** `path` narrowed by `?bundleId=` to one bundle where a colleague has several; absent = the only one. */
export function withBundleId(path: string, bundleId?: string): string {
	return bundleId ? `${path}?bundleId=${encodeURIComponent(bundleId)}` : path;
}

/** Service-only: records one bundle's sent warning (PUT) or retires it (DELETE); `bundleId` picks one. */
export function bundleNoticePath(bundleType: string, slug: string, bundleId?: string): string {
	return withBundleId(
		`${bundleLifecyclePath(bundleType)}/${encodeURIComponent(slug)}/notice`,
		bundleId,
	);
}

/** Service-only: the bundle keys of the platform-ops digest just delivered for a type (PUT). */
export function bundleDigestPath(bundleType: string): string {
	return `${bundleLifecyclePath(bundleType)}/digest`;
}

/** Body of the digest PUT: the keys of the bundles the delivered digest listed. */
export type BundleDigestRequest = { bundles: string[] };

/** Body of the notice PUT. `sentAt` is taken just before the send: a connection after it answers it. */
export type BundleNoticeRequest = { stage: BundleNoticeStage; deadline: string; sentAt: string };

/** How far before the server's clock a notice's `sentAt` may be: one run's send-to-record gap. */
export const BUNDLE_NOTICE_MAX_SEND_AGE_MS = 60 * 60 * 1000;

/** Response of the notice routes. `changed`: false when the row already said this. */
export type BundleNoticeResponse = Pick<
	BundleLifecycleFacts,
	"lifecycle" | "noticeStage" | "noticeStageAt" | "noticeDeadline"
> & { changed: boolean };

/** Every way a body fails to be a {@link BundleNoticeRequest}. Empty = valid. */
export function bundleNoticeRequestErrors(value: unknown): string[] {
	if (typeof value !== "object" || value === null) return ["body must be an object"];
	const r = value as Record<string, unknown>;
	const errors: string[] = [];
	if (!isBundleNoticeStage(r.stage)) errors.push("stage is not a known notice stage");
	if (!isIso(r.deadline)) errors.push("deadline must be ISO-8601");
	if (!isIso(r.sentAt)) errors.push("sentAt must be ISO-8601");
	return errors;
}

/**
 * Service-only append (POST) to the reclamation log. An admin's GET reads it back as a
 * {@link MyBundleReclamationsResponse}, narrowed by `slug`, `bundleType` and `bundleId` query parameters.
 */
export const BUNDLE_RECLAMATIONS_PATH = "/reclamations";

/** The signed-in caller's own reclamation rows. */
export const BUNDLE_RECLAMATIONS_MINE_PATH = "/reclamations/mine";
